import { FC, memo, useState, useMemo, useContext } from 'react';
import { Modal, Box, Typography, IconButton, Button, Radio, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ToastMessageContext } from '@context';

// Import unified channel logo components
import { ChannelLogo, ChannelLogoType } from '../ChannelLogos';

import { T_KEYS } from '../../constants';
import { ChannelType, IApiChannelUnit, IChannelMappingResult } from '../../types';
import { useMapUnits, MappingError } from '../../services/mapping.service';
import PlaceholderImage from '../UnitsMapping/PlaceholderImage';
import MappingErrorModal from '../UnitsMapping/MappingErrorModal';

// Styled Components
const ModalWrapper = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const ModalContainer = styled(Box)({
  width: '680px',
  maxHeight: '80vh',
  backgroundColor: '#ffffff',
  borderRadius: '24px',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 32px',
  borderBottom: '1px solid #f0f0f0',
});

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const ChannelIcon = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '18px',
    height: '18px',
    objectFit: 'contain',
  },
});

const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#101828',
  lineHeight: '28px',
  fontFamily: '"Tajawal", sans-serif',
});

const CloseButtonWrapper = styled(IconButton)({
  padding: '8px',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
});

const ModalDescription = styled(Box)({
  padding: '16px 32px',
  borderBottom: '1px solid #f0f0f0',
});

const DescriptionText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#666666',
  lineHeight: '20px',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'right',
});

const TableContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  maxHeight: '400px',
});

const TableHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 32px',
  borderBottom: '1px solid #e5e7eb',
  backgroundColor: '#fafafa',
});

const TableHeaderCell = styled(Typography)<{ flex?: number; width?: string }>(
  ({ flex = 1, width }) => ({
    flex: width ? 'none' : flex,
    width: width || 'auto',
    fontSize: '12px',
    fontWeight: 700,
    color: '#37352f',
    lineHeight: '16px',
    fontFamily: '"Tajawal", sans-serif',
    textAlign: 'right',
  })
);

const TableRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderBottom: '1px solid #e5e7eb',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#fafafa',
  },
});

const TableCell = styled(Box)<{ flex?: number; width?: string }>(
  ({ flex = 1, width }) => ({
    flex: width ? 'none' : flex,
    width: width || 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  })
);

const PropertyInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  justifyContent: 'flex-start',
});

const PropertyName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#331860',
  lineHeight: '20px',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'right',
});

const PropertyId = styled(Typography)({
  fontSize: '12px',
  fontWeight: 400,
  color: '#262626',
  lineHeight: '16px',
  fontFamily: '"Tajawal", sans-serif',
});

const StatusBadge = styled(Box)<{ $bg?: string }>(({ $bg }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '2px 8px',
  borderRadius: '9999px',
  backgroundColor: $bg || '#f3f4f6',
}));

const StatusDot = styled(Box)<{ $color?: string }>(({ $color }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: $color || '#6b7280',
}));

const StatusText = styled(Typography)<{ $color?: string }>(({ $color }) => ({
  fontSize: '12px',
  fontWeight: 400,
  color: $color || '#6b7280',
  lineHeight: '16px',
  fontFamily: '"Tajawal", sans-serif',
}));

const ModalFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  padding: '24px 32px',
  borderTop: '1px solid #f0f0f0',
});

const CancelButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  border: '1px solid #e4e4e7',
  backgroundColor: '#ffffff',
  color: '#1a1a1a',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: '"Tajawal", sans-serif',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
});

const ConfirmButton = styled(Button)<{ disabled?: boolean }>(({ disabled }) => ({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: disabled ? '#ddd' : '#5B3FFF',
  color: disabled ? '#bdbdbd' : '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: '"Tajawal", sans-serif',
  '&:hover': {
    backgroundColor: disabled ? '#ddd' : '#4a2fd9',
  },
}));

export interface SelectUnitsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  channelType: ChannelType;
  units: IApiChannelUnit[];
  propertyId?: number | null;
  propertyName?: string;
}

const getChannelInfo = (channelType: ChannelType): { name: string; logoType: ChannelLogoType } => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return { name: 'Airbnb', logoType: 'airbnb' };
    case ChannelType.Booking:
      return { name: 'Booking', logoType: 'booking' };
    case ChannelType.Gathern:
      return { name: 'Gathern', logoType: 'gathern' };
    default:
      return { name: '', logoType: 'airbnb' };
  }
};

const SelectUnitsModal: FC<SelectUnitsModalProps> = memo(({
  open,
  onClose,
  onSuccess,
  channelType,
  units,
  propertyId,
  propertyName,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [selectedUnit, setSelectedUnit] = useState<IApiChannelUnit | null>(null);
  const [showMappingErrorModal, setShowMappingErrorModal] = useState(false);
  const [mappingErrorResult, setMappingErrorResult] = useState<IChannelMappingResult | null>(null);
  const { showSuccess, showError } = useContext(ToastMessageContext);
  
  // Use the mapping API
  const { mutate: mapUnits, isPending: isMapping } = useMapUnits();

  const channelInfo = getChannelInfo(channelType);

  // Color mapping for ImportedListingStatus statusName values
  const getStatusColors = (statusName: string | null): { bg: string; text: string } => {
    switch (statusName?.toLowerCase()) {
      case 'available':
        return { bg: '#ECFDF5', text: '#059669' };
      case 'unavailable':
      case 'blocked':
        return { bg: '#FEE2E2', text: '#DC2626' };
      case 'booked':
        return { bg: '#E0E7FF', text: '#4F46E5' };
      case 'pending':
        return { bg: '#FEF3C7', text: '#D97706' };
      case 'imported':
        return { bg: '#5B2FF91A', text: '#5B2FF9' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  // AR translations for known ImportedListingStatus values
  const getStatusText = (statusName: string | null): string => {
    if (!statusName) return t(T_KEYS.NOT_LINKED);
    if (!isRtl) return statusName;
    // AR translations for known status values
    switch (statusName.toLowerCase()) {
      case 'available':
        return 'متاح';
      case 'unavailable':
        return 'غير متاح';
      case 'blocked':
        return 'محظور';
      case 'booked':
        return 'محجوز';
      case 'pending':
        return 'قيد الانتظار';
      case 'imported':
        return 'تم الاستيراد';
      default:
        return statusName;
    }
  };

  // Filter only unmerged units
  const unmergedUnits = useMemo(() => {
    return units.filter(unit => !unit.isMapped);
  }, [units]);

  const handleRowClick = (unit: IApiChannelUnit) => {
    if (isMapping) return;
    setSelectedUnit(unit);
  };

  const handleConfirm = () => {
    if (!selectedUnit || isMapping) return;
    
    // Build the mapping request payload
    const mappingRequest = [{
      userChannelId: selectedUnit.userChannelId,
      channelType: selectedUnit.channelType,
      channelListingId: selectedUnit.channelListingId,
      propertyId: propertyId ?? null,
    }];
    
    mapUnits(mappingRequest, {
      onSuccess: () => {
        showSuccess(t(T_KEYS.UNITS_MAPPING_SUCCESS_MERGED));
        setSelectedUnit(null);
        onClose();
        onSuccess?.();
      },
      onError: (error: any) => {
        // Check if it's a MappingError with detailed result
        if (error instanceof MappingError && error.result?.channelResults) {
          const failedResult = error.result.channelResults.find(r => !r.success);
          if (failedResult) {
            setMappingErrorResult(failedResult);
            setShowMappingErrorModal(true);
            return;
          }
        }
        // Fallback to generic error toast
        showError(error?.message || t('ERRORS.SOMETHING_WENT_WRONG'));
      },
    });
  };

  const handleClose = () => {
    if (isMapping) return;
    setSelectedUnit(null);
    onClose();
  };

  const handleCloseMappingErrorModal = () => {
    setShowMappingErrorModal(false);
    setMappingErrorResult(null);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="select-units-modal"
      >
        <ModalWrapper>
          <ModalContainer>
          <ModalHeader>
            <HeaderLeft>
              <ChannelLogo channel={channelInfo.logoType} size="xs" />
              <HeaderTitle>
                {propertyName 
                  ? t(T_KEYS.LINK_UNIT_FROM_CHANNEL, { channel: channelInfo.name, property: propertyName })
                  : t(T_KEYS.ADD_UNIT_FROM_CHANNEL, { channel: channelInfo.name })
                }
              </HeaderTitle>
            </HeaderLeft>
            <CloseButtonWrapper onClick={handleClose}>
              <Close sx={{ fontSize: 20 }} />
            </CloseButtonWrapper>
          </ModalHeader>

          <ModalDescription>
            <DescriptionText>
              {t(T_KEYS.SELECT_UNIT_DESCRIPTION, { channel: channelInfo.name })}
            </DescriptionText>
          </ModalDescription>

          <TableContainer>
            <TableHeader>
              <TableHeaderCell flex={2}>{t(T_KEYS.PROPERTY)}</TableHeaderCell>
              <TableHeaderCell flex={1}>{t(T_KEYS.IDENTIFIER)}</TableHeaderCell>
              <TableHeaderCell width="100px">{t(T_KEYS.STATUS)}</TableHeaderCell>
            </TableHeader>

            {unmergedUnits.map((unit) => {
              const statusColors = getStatusColors(unit.statusName);
              const statusText = getStatusText(unit.statusName);
              return (
                <TableRow key={unit.id} onClick={() => handleRowClick(unit)}>
                  <TableCell flex={2}>
                    <PropertyInfo>
                      <Radio
                        checked={selectedUnit?.id === unit.id}
                        sx={{
                          marginInlineStart: '16px',
                          '&.Mui-checked': {
                            color: '#5B3FFF',
                          },
                        }}
                        />
                      <PlaceholderImage
                        src={unit.listingImage}
                        alt={unit.listingName ?? ''}
                        fallbackText={unit.listingName?.charAt(0)}
                        />
                      <PropertyName>{unit.listingName}</PropertyName>
                    </PropertyInfo>
                  </TableCell>
                  <TableCell flex={1}>
                    <PropertyId>{unit.id}</PropertyId>
                  </TableCell>
                  <TableCell width="100px">
                    <StatusBadge $bg={statusColors.bg}>
                      <StatusDot $color={statusColors.text} />
                      <StatusText $color={statusColors.text}>{statusText}</StatusText>
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableContainer>

          <ModalFooter>
            <ConfirmButton
              disabled={!selectedUnit || isMapping}
              onClick={handleConfirm}
            >
              {isMapping ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                propertyName ? t(T_KEYS.LINK_UNIT) : t(T_KEYS.ADD_UNIT)
              )}
            </ConfirmButton>
            <CancelButton onClick={handleClose} disabled={isMapping}>
              {t(T_KEYS.CANCEL)}
            </CancelButton>
          </ModalFooter>
          </ModalContainer>
        </ModalWrapper>
      </Modal>

      {/* Mapping Error Modal */}
      <MappingErrorModal
        open={showMappingErrorModal}
        onClose={handleCloseMappingErrorModal}
        failedResult={mappingErrorResult}
      />
    </>
  );
});

SelectUnitsModal.displayName = 'SelectUnitsModal';

export default SelectUnitsModal;
